import { useEffect, useState, useRef } from "react";
import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useAuth } from "../contexts/authContext";

export default function AddToMealPlanModal({ recipeId, isOpen, onClose }) {
  const [mealPlans, setMealPlans] = useState([]);
  const [search, setSearch] = useState("");
  const [newMealName, setNewMealName] = useState("");
  const modalRef = useRef();
  const { token, isLoggedIn } = useAuth();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);

      apiClient.get(`/mealplans`).then(res => setMealPlans(res.data));
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const handleAdd = async (planId) => {
    await apiClient.put(`/mealplans/${planId}`, {
      recipeId,
    });
    toast.success(`Added to mealplan`);
    onClose();
  };

  const handleCreate = async () => {
    if (!newMealName.trim()) return;
    const res = await apiClient.post(`/mealplans`, {
      name: newMealName,
      recipeIds: [recipeId],
    });
    setMealPlans(prev => [...prev, res.data]);
    setNewMealName("");
    toast.success(`Created and added to '${res.data.name}'`);
    onClose();
  };

  const filteredPlans = search
    ? mealPlans.filter(plan =>
        plan.name.toLowerCase().includes(search.toLowerCase())
      )
    : mealPlans.slice(-3).reverse(); // show 3 most recent if no search

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-40 px-4 py-8 overflow-y-auto">
      <div ref={modalRef} className="bg-white p-4 rounded shadow-lg w-full max-w-md mx-auto relative space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Add to Meal Plan</h2>
        <button
            onClick={onClose}
            className="text-gray-400 hover:text-black"
            title="Close"
        >
            <XMarkIcon className="h-5 w-5" />
        </button>
        </div>
        <input
          type="text"
          placeholder="Search meal plans..."
          className="border px-3 py-2 rounded w-full text-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="space-y-2 max-h-48 overflow-y-auto">
        {!search && (
            <p className="text-sm text-gray-500 font-medium mb-1">Recent</p>
        )}
          {filteredPlans.length > 0 ? (
            filteredPlans.map(plan => (
              <button
                key={plan._id}
                onClick={() => handleAdd(plan._id)}
                className="block w-full text-left px-4 py-2 border rounded hover:bg-sage-100"
              >
                {plan.name}
              </button>
            ))
          ) : (
            <p className="text-sm text-gray-500">
              {search ? "No matching meal plans" : "No meal plans yet"}
            </p>
          )}
        </div>

        <div className="pt-4 border-t space-y-2">
          <input
            type="text"
            placeholder="New meal plan name"
            className="border px-3 py-2 rounded w-full text-sm"
            value={newMealName}
            onChange={(e) => setNewMealName(e.target.value)}
          />
          <button
            onClick={handleCreate}
            className="w-full bg-sage text-white rounded-3xl py-2 text-sm hover:bg-sagedark"
          >
            Create & Add
          </button>
        </div>        
      </div>
    </div>
  );
}
